/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React, { useState } from 'react';

import { InvoiceModel } from '../../helpers/invoice-data';
import { IInvoiceErrorState } from '../../invoices-list';
import { IInvoicesTableHeaderResources, InvoicesTableHeaderComponent } from './components/invoices-table-header-component';
import { IInvoicesTableRowResources, InvoicesTableRowComponent } from './components/invoices-table-row-component';

interface IInvoicesTableData {
    invoices: InvoiceModel[];
    customer: Customer;
}

export interface IInvoicesTableResources extends IInvoicesTableHeaderResources, IInvoicesTableRowResources {
}

export interface IInvoicesTableProps extends IComponentProps<IInvoicesTableData> {
    context: ICoreContext;
    className: string;
    invoiceButtonReferences?: React.RefObject<HTMLButtonElement>[];
    payInvoiceButtonReferences?: React.RefObject<HTMLButtonElement>[];
    resources: IInvoicesTableResources;

    onChange(): void;
    onError?(errorState: IInvoiceErrorState, reference?: React.RefObject<HTMLButtonElement>): void;
    onRequestInvoice(invoice: InvoiceModel, requestInvoiceButtonReference: React.RefObject<HTMLButtonElement>): void;
}

export interface IInvoicesTableComponent extends IComponent<IInvoicesTableProps> {
}

type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const getViewport = (context: ICoreContext): GridSize => {
    // Always render in mobile viewport on a mobile device
    if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
        return 'xs';
    }

    if (window?.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                return 'xs';
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                return 'sm';
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                return 'md';
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return 'lg';
            }
            return 'xl';

        }
    }

    return 'xs';
};

const isMobileView = (context: ICoreContext) => {
    const viewport = getViewport(context);
    return viewport === 'xs' || viewport === 'sm';
};

/**
 * On select all function.
 * @param props -Invoice table props.
 * @returns On change.
 */
const onSelectAllHandler = (props: IInvoicesTableProps) => (isSelected: boolean) => {
    props.data.invoices.forEach((invoice) => {
        invoice.isSelected = isSelected;
    });
    props.onChange();
};

const InvoicesTable: React.FC<IInvoicesTableProps> = (props: IInvoicesTableProps): JSX.Element => {
    const [isMobile, setIsMobile] = useState(isMobileView(props.context));

    const updateViewport = () => {
        setIsMobile(isMobileView(props.context));
    };

    if (typeof window !== undefined && window.addEventListener) {
        window.addEventListener('resize', updateViewport);
    }
    const rowClassName = `${props.className}__row`;
    const headerClassName = `${props.className}__header`;

    return (
        <table className={props.className}>
            <InvoicesTableHeaderComponent
                context={props.context}
                typeName={props.typeName}
                id={headerClassName}
                className={headerClassName}
                isSelectedAll={ArrayExtensions.all(props.data.invoices, (invoice: InvoiceModel) => invoice.isSelected)}
                isMobile={isMobile}
                isAdmin={props.data.customer.IsB2bAdmin || false}
                onSelectAll={onSelectAllHandler(props)}
                resources={props.resources}
                data={{}}
            />
            {
                props.data.invoices.map((invoice, index: number) => {
                    return (
                        <InvoicesTableRowComponent
                            context={props.context}
                            typeName={props.typeName}
                            id={`${rowClassName}-${index}`}
                            key={index}
                            className={rowClassName}
                            resources={props.resources}
                            data={{ invoice, index, customer: props.data.customer }}
                            onChange={props.onChange}
                            isMobile={isMobile}
                            onError={props.onError}
                            invoiceReferences={props.invoiceButtonReferences && props.invoiceButtonReferences[Number(index)]}
                            payInvoiceReferences={props.payInvoiceButtonReferences && props.payInvoiceButtonReferences[Number(index)]}
                            onRequestInvoice={props.onRequestInvoice}
                        />
                    );
                })
            }
        </table>
    );
};

// @ts-expect-error
export const InvoicesTableComponent: React.FunctionComponent<IInvoicesTableProps> = msdyn365Commerce.createComponent<IInvoicesTableComponent>(
    'InvoicesTableComponent',
    { component: InvoicesTable }
);
